'use client';

import { useWindowVirtualizer } from '@tanstack/react-virtual';
import {
    CSSProperties,
    memo,
    ReactNode,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    useSyncExternalStore,
} from 'react';
import { CUSTOMER_NOTIFICATIONS_CONTACT_LIMIT } from '@/entities/customer-notification/api/customer-notifications/constants';
import type { CustomerNotification } from '@/entities/customer-notification/api/customer-notifications/types';
import { CustomerNotificationItemProvider } from '@/features/news/customer-notification-item-context';
import { useCustomerNotificationsList } from '@/features/news/useCustomerNotificationsList';
import { useNewsUnread } from '@/features/news/useNewsUnread';
import { findNewsItemIndex, NewsScrollAnchor, splitNewsColumns } from './build-news-feed-rows';
import { collectFeedSizes } from './collect-feed-sizes';
import { isViewportChromeResize } from './is-viewport-chrome-resize';
import { CustomerNotificationsFilters } from './types';

/** Match contact.scss `@media (min-width: 48rem)` two-column breakpoint. */
const TWO_COL_MIN = 768;
const RESIZE_DEBOUNCE_MS = 120;
// ponytail: fallback only — real sizes come from premeasure; keep ≥ card with 12rem media.
const ESTIMATE_ITEM = 400;
const ITEM_GAP = '0.875rem';

type Props = {
    filters?: CustomerNotificationsFilters;
    children?: ReactNode;
};

const readColumnCount = (): 1 | 2 => {
    if (typeof window === 'undefined') return 1;
    return window.matchMedia(`(min-width: ${TWO_COL_MIN}px)`).matches ? 2 : 1;
};

const subscribeColumnCount = (onStoreChange: () => void) => {
    const mq = window.matchMedia(`(min-width: ${TWO_COL_MIN}px)`);
    mq.addEventListener('change', onStoreChange);
    return () => mq.removeEventListener('change', onStoreChange);
};

const useNewsColumnCount = () => useSyncExternalStore(subscribeColumnCount, readColumnCount, (): 1 | 2 => 1);

const NewsFeedItemView = memo(function NewsFeedItemView({
    notification,
    orderIndex,
    isUnread,
    children,
}: {
    notification: CustomerNotification;
    orderIndex: number;
    isUnread: boolean;
    children: ReactNode;
}) {
    return (
        <div className="contact__notifications__item" style={{ '--order': orderIndex } as CSSProperties}>
            <CustomerNotificationItemProvider notification={notification} isUnread={isUnread}>
                {children}
            </CustomerNotificationItemProvider>
        </div>
    );
});

type MeasureNewsItemProps = {
    notification: CustomerNotification;
    orderIndex: number;
    isUnread: boolean;
    children: ReactNode;
};

/** Same outer box as VirtualNewsItem (incl. gap) so premeasure matches virtual rows. */
const MeasureNewsItem = memo(function MeasureNewsItem({
    notification,
    orderIndex,
    isUnread,
    children,
}: MeasureNewsItemProps) {
    return (
        <div data-feed-key={notification.id} style={{ marginBottom: ITEM_GAP }}>
            <NewsFeedItemView notification={notification} orderIndex={orderIndex} isUnread={isUnread}>
                {children}
            </NewsFeedItemView>
        </div>
    );
});

type VirtualNewsItemProps = {
    notification: CustomerNotification;
    orderIndex: number;
    index: number;
    start: number;
    scrollMargin: number;
    isUnread: boolean;
    children: ReactNode;
    measureElement: (node: Element | null) => void;
};

const VirtualNewsItem = memo(function VirtualNewsItem({
    notification,
    orderIndex,
    index,
    start,
    scrollMargin,
    isUnread,
    children,
    measureElement,
}: VirtualNewsItemProps) {
    return (
        <div
            data-index={index}
            ref={measureElement}
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${start - scrollMargin}px)`,
            }}
        >
            <div style={{ marginBottom: ITEM_GAP }}>
                <NewsFeedItemView notification={notification} orderIndex={orderIndex} isUnread={isUnread}>
                    {children}
                </NewsFeedItemView>
            </div>
        </div>
    );
});

type ColumnVirtualizer = ReturnType<typeof useWindowVirtualizer>;

const VirtualColumn = ({
    items,
    orderStart,
    scrollMargin,
    virtualizer,
    isUnread,
    children,
    testId,
}: {
    items: CustomerNotification[];
    /** 0 = left (even global indices), 1 = right (odd). */
    orderStart: 0 | 1;
    scrollMargin: number;
    virtualizer: ColumnVirtualizer;
    isUnread: (item: CustomerNotification) => boolean;
    children: ReactNode;
    testId: string;
}) => (
    <div
        className="contact__notifications__column"
        data-testid={testId}
        style={{
            position: 'relative',
            flex: 1,
            minWidth: 0,
            height: virtualizer.getTotalSize(),
        }}
    >
        {virtualizer.getVirtualItems().map((vItem) => {
            const notification = items[vItem.index];
            if (!notification) return null;
            return (
                <VirtualNewsItem
                    key={notification.id}
                    notification={notification}
                    orderIndex={orderStart + vItem.index * 2}
                    index={vItem.index}
                    start={vItem.start}
                    scrollMargin={scrollMargin}
                    isUnread={isUnread(notification)}
                    measureElement={virtualizer.measureElement}
                >
                    {children}
                </VirtualNewsItem>
            );
        })}
    </div>
);

export const CustomerNotificationsFor = ({ filters, children }: Props) => {
    // ponytail: single List fetch — InfiniteList + Server Action remounted the page (GET+POST loop).
    // Upgrade path: Route Handler GET for pages, then restore sentinel infinite scroll.
    const { items } = useCustomerNotificationsList({
        venueId: filters?.venueId,
        limit: filters?.limit ?? CUSTOMER_NOTIFICATIONS_CONTACT_LIMIT,
    });
    const { isUnread } = useNewsUnread();
    const columnCount = useNewsColumnCount();
    const twoCol = columnCount === 2;

    // CSS feed-root handles 1↔2 cols before JS hydrates columnCount (server snapshot is 1).
    const [columnsReady, setColumnsReady] = useState(false);
    const [virtualize, setVirtualize] = useState(false);
    const [scrollMargin, setScrollMargin] = useState(0);
    const listRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setColumnsReady(true);
    }, []);

    const [left, right] = useMemo(() => splitNewsColumns(items), [items]);
    const leftRef = useRef(left);
    const rightRef = useRef(right);
    const itemsRef = useRef(items);
    leftRef.current = left;
    rightRef.current = right;
    itemsRef.current = items;

    const itemsKey = useMemo(() => items.map((item) => item.id).join('\0'), [items]);
    const layoutKey = `${itemsKey}:${columnCount}`;

    const pendingAnchorRef = useRef<NewsScrollAnchor | null>(null);
    const columnCountRef = useRef(columnCount);
    const layoutKeyWhileVirtualRef = useRef(layoutKey);
    const sizeByKeyRef = useRef(new Map<string, number>());

    const singleVirtualizerRef = useRef<ColumnVirtualizer | null>(null);
    const leftVirtualizerRef = useRef<ColumnVirtualizer | null>(null);
    const rightVirtualizerRef = useRef<ColumnVirtualizer | null>(null);

    const estimateFromCache = useCallback(
        (id: string | undefined) => (id ? (sizeByKeyRef.current.get(id) ?? ESTIMATE_ITEM) : ESTIMATE_ITEM),
        [],
    );

    const captureAnchor = useCallback((): NewsScrollAnchor | null => {
        const scrollY = window.scrollY;
        const tryList = (virtualizer: ColumnVirtualizer | null, list: CustomerNotification[]) => {
            if (!virtualizer || list.length === 0) return null;
            for (const vItem of virtualizer.getVirtualItems()) {
                const item = list[vItem.index];
                if (!item || vItem.end <= scrollY) continue;
                return {
                    id: item.id,
                    offsetWithinRow: Math.max(0, scrollY - vItem.start),
                };
            }
            return null;
        };

        if (columnCountRef.current === 1) {
            return tryList(singleVirtualizerRef.current, itemsRef.current);
        }
        return (
            tryList(leftVirtualizerRef.current, leftRef.current) ??
            tryList(rightVirtualizerRef.current, rightRef.current)
        );
    }, []);

    const restoreAnchor = useCallback((anchor: NewsScrollAnchor | null) => {
        if (!anchor) return;

        const restoreIn = (virtualizer: ColumnVirtualizer | null, list: CustomerNotification[]) => {
            if (!virtualizer) return false;
            const index = findNewsItemIndex(list, anchor.id);
            if (index === null) return false;
            virtualizer.measure();
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    const offset = virtualizer.getOffsetForIndex(index, 'start');
                    if (!offset) return;
                    virtualizer.scrollToOffset(offset[0] + anchor.offsetWithinRow);
                });
            });
            return true;
        };

        if (columnCountRef.current === 1) {
            restoreIn(singleVirtualizerRef.current, itemsRef.current);
            return;
        }
        if (restoreIn(leftVirtualizerRef.current, leftRef.current)) return;
        restoreIn(rightVirtualizerRef.current, rightRef.current);
    }, []);

    // Full list → measure (same box as virtual, incl. gap) → virtualize.
    // Wait for columnsReady so we never premeasure the hydration columnCount=1 snapshot.
    useLayoutEffect(() => {
        if (!columnsReady) return;

        if (virtualize) {
            if (layoutKeyWhileVirtualRef.current !== layoutKey) {
                layoutKeyWhileVirtualRef.current = layoutKey;
                pendingAnchorRef.current = captureAnchor();
                // Defer — setState here can re-enter TanStack notify/flushSync mid-layout.
                queueMicrotask(() => setVirtualize(false));
            }
            return;
        }

        const root = listRef.current;
        if (!root) return;
        sizeByKeyRef.current = collectFeedSizes(root);
        setScrollMargin(root.offsetTop);
        layoutKeyWhileVirtualRef.current = layoutKey;
        columnCountRef.current = columnCount;
        setVirtualize(true);
    }, [columnsReady, virtualize, layoutKey, columnCount, captureAnchor]);

    // After virtualize flips on, restore scroll outside layout (TanStack measure → flushSync).
    useEffect(() => {
        if (!virtualize || !pendingAnchorRef.current) return;
        const anchor = pendingAnchorRef.current;
        pendingAnchorRef.current = null;
        restoreAnchor(anchor);
    }, [virtualize, restoreAnchor]);

    useLayoutEffect(() => {
        if (!listRef.current || !virtualize) return;
        const top = listRef.current.offsetTop;
        if (top !== scrollMargin) setScrollMargin(top);
    }, [items.length, columnCount, scrollMargin, virtualize]);

    const singleVirtualizer = useWindowVirtualizer({
        count: items.length,
        estimateSize: (index) => estimateFromCache(items[index]?.id),
        overscan: 4,
        scrollMargin,
        getItemKey: (index) => items[index]?.id ?? index,
        enabled: virtualize && !twoCol,
        useFlushSync: false,
    });
    singleVirtualizerRef.current = singleVirtualizer;

    const leftVirtualizer = useWindowVirtualizer({
        count: left.length,
        estimateSize: (index) => estimateFromCache(left[index]?.id),
        overscan: 4,
        scrollMargin,
        getItemKey: (index) => left[index]?.id ?? index,
        enabled: virtualize && twoCol,
        useFlushSync: false,
    });
    leftVirtualizerRef.current = leftVirtualizer;

    const rightVirtualizer = useWindowVirtualizer({
        count: right.length,
        estimateSize: (index) => estimateFromCache(right[index]?.id),
        overscan: 4,
        scrollMargin,
        getItemKey: (index) => right[index]?.id ?? index,
        enabled: virtualize && twoCol,
        useFlushSync: false,
    });
    rightVirtualizerRef.current = rightVirtualizer;

    useEffect(() => {
        if (!virtualize) return;
        let lastW = window.innerWidth;
        let lastH = window.innerHeight;
        let timer: ReturnType<typeof setTimeout> | undefined;
        const onResize = () => {
            const w = window.innerWidth;
            const h = window.innerHeight;
            if (isViewportChromeResize(lastW, lastH, w, h)) {
                lastH = h;
                return;
            }
            lastW = w;
            lastH = h;

            if (!pendingAnchorRef.current) {
                pendingAnchorRef.current = captureAnchor();
            }
            clearTimeout(timer);
            timer = setTimeout(() => {
                if (columnCountRef.current !== readColumnCount()) return;
                setVirtualize(false);
            }, RESIZE_DEBOUNCE_MS);
        };
        window.addEventListener('resize', onResize);
        return () => {
            clearTimeout(timer);
            window.removeEventListener('resize', onResize);
        };
    }, [virtualize, captureAnchor]);

    useEffect(() => {
        if (!virtualize) return;
        const mq = window.matchMedia(`(min-width: ${TWO_COL_MIN}px)`);
        const stash = () => {
            pendingAnchorRef.current = captureAnchor();
        };
        mq.addEventListener('change', stash);
        return () => mq.removeEventListener('change', stash);
    }, [virtualize, captureAnchor]);

    const renderMeasureItems = (list: CustomerNotification[], startIndex: 0 | 1 | 'flat') =>
        list.map((notification, i) => (
            <MeasureNewsItem
                key={notification.id}
                notification={notification}
                orderIndex={startIndex === 'flat' ? i : startIndex + i * 2}
                isUnread={isUnread(notification)}
            >
                {children}
            </MeasureNewsItem>
        ));

    const renderCssColumnItems = (list: CustomerNotification[], startIndex: 0 | 1) =>
        list.map((notification, i) => (
            <NewsFeedItemView
                key={notification.id}
                notification={notification}
                orderIndex={startIndex + i * 2}
                isUnread={isUnread(notification)}
            >
                {children}
            </NewsFeedItemView>
        ));

    return (
        <div ref={listRef} style={{ position: 'relative', width: '100%' }}>
            {!columnsReady ? (
                <div className="contact__notifications__feed-root">
                    <div
                        className="contact__notifications__column"
                        data-testid="contact-notifications-column-left"
                    >
                        {renderCssColumnItems(left, 0)}
                    </div>
                    <div
                        className="contact__notifications__column"
                        data-testid="contact-notifications-column-right"
                    >
                        {renderCssColumnItems(right, 1)}
                    </div>
                </div>
            ) : !virtualize ? (
                twoCol ? (
                    <div
                        className="contact__notifications__feed-root"
                        style={{ display: 'flex', flexDirection: 'row', gap: '1.5rem', width: '100%' }}
                    >
                        <div
                            className="contact__notifications__column"
                            data-testid="contact-notifications-column-left"
                            style={{ flex: 1, minWidth: 0 }}
                        >
                            {renderMeasureItems(left, 0)}
                        </div>
                        <div
                            className="contact__notifications__column"
                            data-testid="contact-notifications-column-right"
                            style={{ flex: 1, minWidth: 0 }}
                        >
                            {renderMeasureItems(right, 1)}
                        </div>
                    </div>
                ) : (
                    <div data-testid="contact-notifications-column-left">
                        {renderMeasureItems(items, 'flat')}
                    </div>
                )
            ) : twoCol ? (
                <div className="contact__notifications__feed-root">
                    <VirtualColumn
                        items={left}
                        orderStart={0}
                        scrollMargin={scrollMargin}
                        virtualizer={leftVirtualizer}
                        isUnread={isUnread}
                        testId="contact-notifications-column-left"
                    >
                        {children}
                    </VirtualColumn>
                    <VirtualColumn
                        items={right}
                        orderStart={1}
                        scrollMargin={scrollMargin}
                        virtualizer={rightVirtualizer}
                        isUnread={isUnread}
                        testId="contact-notifications-column-right"
                    >
                        {children}
                    </VirtualColumn>
                </div>
            ) : (
                <div
                    style={{
                        height: singleVirtualizer.getTotalSize(),
                        width: '100%',
                        position: 'relative',
                    }}
                >
                    {singleVirtualizer.getVirtualItems().map((vItem) => {
                        const notification = items[vItem.index];
                        if (!notification) return null;
                        return (
                            <VirtualNewsItem
                                key={notification.id}
                                notification={notification}
                                orderIndex={vItem.index}
                                index={vItem.index}
                                start={vItem.start}
                                scrollMargin={scrollMargin}
                                isUnread={isUnread(notification)}
                                measureElement={singleVirtualizer.measureElement}
                            >
                                {children}
                            </VirtualNewsItem>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
